import { ComponentProps } from 'preact';
import { useState, useCallback } from 'preact/hooks';
import { RefObject } from 'preact/compat';
import { Menu } from '../../UNSAFE_Menu';
import { BaseButton } from '../../UNSAFE_BaseButton';

type InitialFocusType = ComponentProps<typeof Menu>['initialFocus'];
type MenuProps = {
  isOpen?: ComponentProps<typeof Menu>['isOpen'];
  anchorRef: ComponentProps<typeof Menu>['anchorRef'];
  initialFocus?: InitialFocusType;
  onClose: ComponentProps<typeof Menu>['onClose'];
};

export type MenuToggleDetail = {
  value: boolean;
};

export type MenuActionOptions = {
  isDisabled?: boolean;
  anchorRef: RefObject<HTMLElement>;
  onToggleMenu?: (details: MenuToggleDetail) => void;
  isMenuOpen?: boolean;
};

export type MenuActionReturnType = {
  triggerProps: Record<string, any>;
  menuProps: MenuProps;
};

/**
 * Use to associate a popup menu with a target that supports onAction
 *
 * @returns
 */
export function useMenuAction({
  isDisabled,
  isMenuOpen,
  onToggleMenu,
  anchorRef
}: MenuActionOptions): MenuActionReturnType {
  const [initialFocus, setInitialFocus] = useState<InitialFocusType>('menu');

  const handleAction = useCallback(
    ((details) => {
      if (details.reason === 'keyboard') {
        setInitialFocus('firstItem');
      } else {
        setInitialFocus('menu');
      }

      onToggleMenu?.({
        value: !isMenuOpen
      });
    }) as Required<ComponentProps<typeof BaseButton>>['onAction'],
    [onToggleMenu, isMenuOpen, setInitialFocus]
  );

  const handleClose = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      if (detail.reason === 'outsideClick' && detail.target === anchorRef.current) {
        return;
      }

      //Focus has to be set on the launcher when menu is dismissed or
      //item is "selected"
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        anchorRef.current?.focus();
      }

      onToggleMenu?.({
        value: false
      });
    },
    [onToggleMenu, anchorRef]
  );

  const menuProps = {
    anchorRef: anchorRef,
    isOpen: isMenuOpen,
    onClose: handleClose,
    initialFocus: initialFocus
  };

  const triggerProps = isDisabled
    ? {}
    : {
        onAction: handleAction,
        'aria-haspopup': 'true',
        'aria-expanded': isMenuOpen
      };

  return {
    triggerProps: triggerProps,
    menuProps: menuProps
  };
}
